/**
 * Authentication context providing user info, roles, and token acquisition.
 *
 * Roles are resolved by acquiring an access token for the W365LithiumMgmt app
 * and parsing the roles claim from the JWT.
 * Falls back to W365LithiumMgmt.Read if no roles are present.
 *
 * @module contexts/AuthContext
 */

import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from '@/config/msalConfig';

export type AppRole = 'W365LithiumMgmt.Read' | 'W365LithiumMgmt.ReadWrite';

export interface AuthUser {
  name: string;
  email: string;
  objectId: string;
  initials: string;
  roles: AppRole[];
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VALID_ROLES = new Set<string>(['W365LithiumMgmt.Read', 'W365LithiumMgmt.ReadWrite']);

/** Decode the payload of a JWT without verification (client-side claim extraction). */
function parseJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) return {};
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(payload));
}

/** Extract roles from a JWT's claims. */
function extractRolesFromToken(token: string): AppRole[] {
  const claims = parseJwtPayload(token);
  if (Array.isArray(claims['roles'])) {
    const tokenRoles = (claims['roles'] as string[]).filter((r) => VALID_ROLES.has(r)) as AppRole[];
    if (tokenRoles.length > 0) return tokenRoles;
  }
  return [];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((p) => p.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [tokenRoles, setTokenRoles] = useState<AppRole[] | null>(null);
  const [rolesFetched, setRolesFetched] = useState(false);

  const account = accounts[0] ?? null;
  const msalLoading = inProgress !== InteractionStatus.None;

  // Acquire access token helper (exposed to consumers)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(tokenRequest);
        return null;
      }
      console.error('[Auth] Token acquisition failed:', error);
      return null;
    }
  }, [instance, account]);

  // Acquire Portal API token and extract roles from the JWT claims
  useEffect(() => {
    if (!isAuthenticated || !account || msalLoading || rolesFetched) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessToken();
        if (cancelled || !token) {
          setRolesFetched(true);
          return;
        }

        const roles = extractRolesFromToken(token);
        console.log('[Auth] Roles from Portal API token:', roles);
        if (roles.length > 0) {
          setTokenRoles(roles);
        }
      } catch (error) {
        console.warn('[Auth] Failed to acquire Portal API token for role resolution:', error);
      } finally {
        if (!cancelled) setRolesFetched(true);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, account, msalLoading, rolesFetched, getAccessToken]);

  // Reset on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setTokenRoles(null);
      setRolesFetched(false);
    }
  }, [isAuthenticated]);

  // Use roles from Portal API token, fall back to Read
  const roles = tokenRoles ?? (rolesFetched ? ['W365LithiumMgmt.Read' as AppRole] : []);

  const user = useMemo<AuthUser | null>(() => {
    if (!account) return null;
    return {
      name: account.name || 'Unknown User',
      email: account.username || '',
      objectId: account.localAccountId || '',
      initials: getInitials(account.name || 'U'),
      roles,
    };
  }, [account, roles]);

  const hasRole = useCallback((role: AppRole) => roles.includes(role), [roles]);
  const isAdmin = useMemo(() => roles.includes('W365LithiumMgmt.ReadWrite'), [roles]);

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/signin` });
  }, [instance]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading: msalLoading || (isAuthenticated && !rolesFetched),
      roles,
      hasRole,
      isAdmin,
      login,
      logout,
      getAccessToken,
    }),
    [user, isAuthenticated, msalLoading, rolesFetched, roles, hasRole, isAdmin, login, logout, getAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
